import { cn } from '@/lib/utils';

/** Drawer 内置关闭按钮：深色霓虹 */
export const drawerCloseBtn =
  '[&>button]:right-5 [&>button]:top-5 [&>button]:rounded-xl [&>button]:border [&>button]:border-white/10 [&>button]:bg-zinc-900/90 [&>button]:p-2 [&>button]:text-zinc-400 [&>button]:opacity-100 [&>button]:hover:border-cyan-500/30 [&>button]:hover:bg-cyan-500/10 [&>button]:hover:text-cyan-200';

const drawerShellBase = cn(
  'flex h-full max-h-dvh flex-col border-l border-cyan-500/20 bg-zinc-950 text-zinc-100 shadow-[0_0_60px_-20px_rgba(34,211,238,0.25)]',
  drawerCloseBtn,
);

/** 右侧管理抽屉外壳（与 data-admin-shell 深色霓虹一致） */
export function adminDrawerShell(size: 'narrow' | 'wide' | 'detail') {
  const w =
    size === 'detail'
      ? 'w-[min(100vw,42rem)] max-w-none'
      : size === 'wide'
        ? 'w-[min(100vw,32rem)] max-w-none'
        : 'w-[min(100vw,26rem)] max-w-none';
  return cn(drawerShellBase, w);
}

export const adminDrawerHeaderClass =
  'shrink-0 gap-1 border-b border-white/10 bg-zinc-950/95 p-0 px-6 pb-3 pt-4 pr-14';

export const adminDrawerTitleClass =
  "font-['Syne',sans-serif] text-xl font-semibold tracking-tight text-zinc-50";

export const adminDrawerFooterClass =
  'flex shrink-0 flex-row gap-2 border-t border-white/10 bg-zinc-950/95 px-6 py-3';

/**
 * 中间表单区：占满标题与底部操作条之间的剩余高度，仅此区域纵向滚动。
 * 底部按钮应放在滚动层外、与该区并列，以保证始终贴抽屉底边。
 */
export const adminDrawerScrollAreaClass =
  'min-h-0 flex-1 overflow-y-auto overscroll-y-contain';

/** 滚动区内表单的内边距（底部略加大，滚到底时不贴操作条） */
export const adminDrawerFormPaddingClass = 'px-6 pt-3 pb-4';

/** 无底部按钮的只读区（如租户详情）：滚动 + 均匀内边距 */
export const adminDrawerBodyClass = cn(
  adminDrawerScrollAreaClass,
  'px-6 py-3',
);

export const btnOutlineNeon = cn(
  'border-zinc-700/80 bg-zinc-950/50 text-zinc-300',
  'hover:border-cyan-500/35 hover:bg-cyan-500/10 hover:text-cyan-50',
  'focus-visible:ring-cyan-400/40 focus-visible:ring-offset-0',
);

/** 含实色背景：Drawer/Dialog 通过 Portal 挂在 body 时不在 data-admin-shell 内，避免仍用 :root 蓝主色 */
export const btnPrimaryNeon = cn(
  'border border-cyan-400/45 bg-cyan-500 text-zinc-950',
  'hover:border-cyan-300/55 hover:bg-cyan-400 hover:text-zinc-950',
  'shadow-[0_0_22px_-10px_rgba(34,211,238,0.45)]',
  'hover:shadow-[0_0_28px_-8px_rgba(34,211,238,0.55)]',
  'focus-visible:ring-2 focus-visible:ring-cyan-400/45 focus-visible:ring-offset-0',
);

/** 表格/列表内次要操作：透明底 + 悬停青字 */
export const btnGhostNeon = cn(
  'text-zinc-400 hover:bg-white/[0.07] hover:text-cyan-200',
  'focus-visible:ring-cyan-400/35 focus-visible:ring-offset-0',
);

/** 卡片右上角仅图标（编辑等） */
export const btnIconGhostNeon = cn(
  'h-9 w-9 text-zinc-500 hover:bg-white/[0.06] hover:text-cyan-200',
  'focus-visible:ring-cyan-400/35 focus-visible:ring-offset-0',
);

/** 卡片右上角删除图标 */
export const btnIconDangerGhost = cn(
  'h-9 w-9 text-zinc-500 hover:bg-red-500/10 hover:text-red-300',
  'focus-visible:ring-red-400/40 focus-visible:ring-offset-0',
);

export const btnDangerOutline = cn(
  'border-red-500/35 bg-zinc-950/50 text-red-300',
  'hover:border-red-400/50 hover:bg-red-500/10 hover:text-red-100',
  'focus-visible:ring-red-400/35 focus-visible:ring-offset-0',
);

export const btnDangerSolid = cn(
  'border border-red-500/40 bg-red-600/85 text-white',
  'hover:border-red-400/60 hover:bg-red-500',
  'shadow-[0_0_24px_-10px_rgba(248,113,113,0.35)] focus-visible:ring-red-400/50 focus-visible:ring-offset-0',
);

export const fieldClass = cn(
  'border-zinc-700/80 bg-zinc-900/60 text-zinc-100 placeholder:text-zinc-600',
  'focus-visible:ring-cyan-500/40 focus-visible:ring-offset-0',
);

export const labelMutedClass = 'text-zinc-400';

export const alertDialogDarkClass = cn(
  'border border-cyan-500/20 bg-zinc-950 text-zinc-100 shadow-[0_0_60px_-20px_rgba(34,211,238,0.2)] sm:rounded-xl',
);

export const alertDialogTitleDark =
  "font-['Syne',sans-serif] text-lg font-semibold text-zinc-50";

export const alertDialogDescDark = 'text-sm text-zinc-400';
